import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../utils/api";
import { Validation } from "../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../@types/snackbarAPI.types";
import { Member } from "../../../../@types/member.types";
import useRouteParam from "../../../../hooks/useRouteParam";
import { Group } from "../../../../@types/group.types";
import { useEmployerRoute } from "../../../../hooks";
import ROLES from "../../../../constants/roles";
import validateEmail from "../../../../utils/commonFunctions/validateEmail";
import { locationNumberUpdatePayloadType } from "../../../../utils/api/memberInfoUpdate/locationNumberUpdate";

export type LocationNumberTypes = {
	group_number: number | null;
	role: string;
	SSN: string;
	location_number: number | null;
};

const chipMessage = "This functionality will update the Location Number of Employee/Dependent of specific group.";

const LocationNumberUpdation = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<LocationNumberTypes>({
		group_number: null,
		role: "",
		SSN: "",
		location_number: null
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, role, SSN, location_number } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (role.length === 0) {
			_validation.current.reportFieldDetails["role"] = "Role is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (SSN.length === 0) {
			_validation.current.reportFieldDetails["SSN"] = "SSN is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (location_number === null) {
			_validation.current.reportFieldDetails["location_number"] = "Location Number is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		// if (SSN !== null) {
		// 	if (SSN !== "") {
		// 		const _SSN = SSN.replaceAll("-", "");
		// 		if (String(_SSN).length !== 9) {
		// 			_validation.current.reportFieldDetails["SSN"] = "Enter correct 9 digit SSN";
		// 			_validation.current["status"] = "invalid";
		// 			flag = false;
		// 		}
		// 	}
		// }
		// if (SSN !== "") {
		// 	const _SSN = SSN.replaceAll("-", "");
		// 	if (String(_SSN).length !== 9) {
		// 		_validation.current.reportFieldDetails["SSN"] = "Enter correct 9 digit SSN";
		// 		_validation.current["status"] = "invalid";
		// 		flag = false;
		// 	}
		// }

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name } = event.target;
			const { value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Location Number Updation");
					const { group_number, role, SSN, location_number } = reportFieldDetails;

					const payload: any = {
						group_number,
						role,
						SSN: SSN.replaceAll("-", ""),
						location_number
					};

					// console.log("Location Number Updation Payload", payload);
					const response: any = await trackPromise(api.memberInfoUpdate.locationNumberUpdate(payload));

					if (response) {
						// console.log("Response", response);
						alert("Location Number Updation Successful");
						window.location.reload();

						// setTimeout(() => {
						// 	setSnackbarAPICallProps(
						// 		Object.assign({}, snackbarAPICallProps, {
						// 			open: true,
						// 			message: "Location Number Updation Successful!",
						// 			severity: "success"
						// 		})
						// 	);
						// }, 2000);
					}
				}
			} catch (error) {
				alert("Location Number Updation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.location_number_updation);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Location Number Updation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Member Role",
							name: "role",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.role,
							type: "select",
							options: [ROLES.employee, ROLES.dependent]
						},
						{
							label: "Member SSN",
							name: "SSN",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Member SSN",
							value: reportFieldDetails.SSN,
							type: "textfield"
						},
						{
							label: "New Location Number",
							name: "location_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "New Location Number",
							value: reportFieldDetails.location_number,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [assignedGroups, reportFieldDetails, handleChange, isEmployerRoute, queryGroupNumber, selectedGroup?.name]);

	// console.log("reportFieldDetails", reportFieldDetails);
	// console.log("Location Number Validation", validation);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={() => null}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default LocationNumberUpdation;
